//Angular imports
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

//Service imports
import { ClientErrorService } from '../_services/shared-services/client-error.service';
import { ServerErrorService } from '../_services/shared-services/server-error.service';
import { DatadogService } from '../_services/datadog.service';
import { StorageKeyConstants } from '../_constants/storage-key-constants';

@Injectable()
export class RPErrorsHandler implements ErrorHandler {
  private static readonly MAX_CHUNK_RELOAD_ATTEMPTS = 2;

  constructor(private readonly injector: Injector) {}

  handleError(error: Error | HttpErrorResponse) {
    // Chunk load error recovery: reload to fetch updated chunks after deployment.
    // Capped per tab session so a persistently broken chunk falls through to
    // normal error handling instead of looping.
    if (this.isChunkLoadError(error)) {
      const attempts =
        Number(
          sessionStorage.getItem(StorageKeyConstants.chunkLoadReloadAttempts)
        ) || 0;
      if (attempts < RPErrorsHandler.MAX_CHUNK_RELOAD_ATTEMPTS) {
        sessionStorage.setItem(
          StorageKeyConstants.chunkLoadReloadAttempts,
          String(attempts + 1)
        );
        window.location.reload();
        return;
      }
    }

    const clientErrorService = this.injector.get(ClientErrorService);
    const serverErrorService = this.injector.get(ServerErrorService);
    const datadogService = this.injector.get(DatadogService);

    if (error instanceof HttpErrorResponse) {
      // Server or connection error
      serverErrorService.displayError(error);
      // HttpErrorResponse errors are reported to Datadog at the interceptor
      // layer by DatadogErrorInterceptor (fires regardless of whether the
      // caller catches the error). Skipping addError here avoids double-
      // counting when an HttpErrorResponse happens to also be unhandled.
    } else {
      // Client Error (Angular Error, ReferenceError...)
      clientErrorService.displayError(error);
      // Only non-HTTP (client-side) errors are reported to Datadog here.
      datadogService.addClientError(error);
    }

    // Log error to console
    console.error(error);
  }

  private isChunkLoadError(error: Error | HttpErrorResponse): boolean {
    if (error instanceof HttpErrorResponse) {
      return false;
    }
    const message = error?.message || '';
    const chunkErrorPattern =
      /Failed to fetch dynamically imported module|Loading chunk [\d]+ failed|error loading dynamically imported module|Importing a module script failed/i;
    return chunkErrorPattern.test(message) || error?.name === 'ChunkLoadError';
  }
}
